// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.
// frontend/src/components/enterprise-admin/connectors/ConnectorActionModal.jsx

import React, { useEffect, useMemo, useState } from "react";
import {
  ActionGroup,
  Alert,
  Button,
  Field,
  Select,
  TextArea,
  TextInput,
} from "../../../design-system/components";
import ModalShell from "../../compliance/modals/shared/ModalShell";
import ModalSection from "../../compliance/modals/shared/ModalSection";

function titleFor(action) {
  if (action === "owner") return "Assign connector owner";
  if (action === "governance") return "Update connector governance";
  if (action === "pause") return "Pause connector governance";
  if (action === "resume") return "Resume connector governance";
  if (action === "disconnect") return "Disconnect connector";
  if (action === "reverify") return "Reverify live connection";
  return "Request connector reauthorization";
}

function eyebrowFor(action) {
  if (action === "disconnect") return "Disconnect connection";
  if (action === "owner") return "Connector ownership";
  if (action === "governance") return "Governance context";
  return "Connector action";
}

function descriptionFor(action, connector) {
  const provider = connector?.provider_name || connector?.provider_key || "This connector";

  if (action === "disconnect") {
    return `${provider} will stop being used for evidence collection. Existing governance, evidence history, and audit events will be retained.`;
  }
  if (action === "reverify") {
    return `${provider} will be contacted using the stored read-only authorization to confirm the connection is still healthy.`;
  }
  if (action === "pause") {
    return "Pause governance activity without deleting the connector, authorization history, or collected evidence.";
  }
  if (action === "resume") {
    return "Resume governed evidence activity for this connector.";
  }
  if (action === "owner") {
    return "Assign an accountable organization member without changing provider authorization.";
  }
  if (action === "governance") {
    return "Document why the connection is authorized and any operating or risk context administrators should know.";
  }
  return provider;
}

function userIdentifier(user) {
  return user?.user_id || user?.id || user?.auth0_user_id || user?.subject || "";
}

function userDisplayName(user) {
  const combined = `${user?.first_name || user?.given_name || ""} ${user?.last_name || user?.family_name || ""}`.trim();
  return (
    user?.display_name ||
    user?.name ||
    user?.full_name ||
    combined ||
    user?.email ||
    userIdentifier(user) ||
    "Organization member"
  );
}

function userSecondaryLabel(user) {
  const email = user?.email || user?.email_address || user?.primary_email;
  const display = userDisplayName(user);
  if (email && email !== display) return email;
  return null;
}

export default function ConnectorActionModal({
  action,
  connector,
  users = [],
  saving,
  error = "",
  onClose,
  onSubmit,
}) {
  const initialOwnerId = connector?.owner_user_id || userIdentifier(users[0]);
  const [reason, setReason] = useState("");
  const [ownerUserId, setOwnerUserId] = useState(initialOwnerId);
  const [businessPurpose, setBusinessPurpose] = useState(connector?.business_purpose || "");
  const [riskNotes, setRiskNotes] = useState(connector?.risk_notes || "");

  useEffect(() => {
    setReason("");
    setOwnerUserId(connector?.owner_user_id || userIdentifier(users[0]));
    setBusinessPurpose(connector?.business_purpose || "");
    setRiskNotes(connector?.risk_notes || "");
  }, [action, connector, users]);

  const selectedUser = useMemo(
    () => users.find((user) => userIdentifier(user) === ownerUserId) || null,
    [users, ownerUserId]
  );

  const destructive = action === "disconnect";
  const needsReason = ["reauthorize", "pause", "resume", "disconnect"].includes(action);
  const provider = connector?.provider_name || connector?.provider_key || "Connector";

  const submit = async (event) => {
    event.preventDefault();

    if (action === "owner") {
      await onSubmit?.(action, {
        owner_user_id: ownerUserId,
        owner_name: selectedUser ? userDisplayName(selectedUser) : ownerUserId,
        owner_email: selectedUser?.email || selectedUser?.email_address || null,
        owner_type: "primary",
      });
      return;
    }

    if (action === "governance") {
      await onSubmit?.(action, {
        business_purpose: businessPurpose.trim(),
        risk_notes: riskNotes.trim(),
      });
      return;
    }

    await onSubmit?.(action, { reason: reason.trim() || null });
  };

  const submitDisabled =
    saving ||
    (action === "owner" && !ownerUserId) ||
    (action === "disconnect" && !reason.trim());

  return (
    <ModalShell
      className="enterprise-admin-modal-shell enterprise-admin-connector-action-shell"
      overlayClassName="enterprise-admin-modal-overlay"
      headerClassName="enterprise-admin-modal-header"
      bodyClassName="enterprise-admin-modal-body"
      open={Boolean(action && connector)}
      eyebrow={eyebrowFor(action)}
      title={titleFor(action)}
      subtitle={provider}
      onClose={saving ? undefined : onClose}
      maxWidth="680px"
      accentBorder={destructive ? "rgba(255, 123, 123, 0.5)" : "rgba(106, 168, 255, 0.42)"}
    >
      <form className="enterprise-admin-connector-action-form" onSubmit={submit}>
        <ModalSection
          title="What will change"
          description={descriptionFor(action, connector)}
          badge="Audited"
          badgeTone={destructive ? "warning" : "info"}
        >
          {action === "owner" ? (
            <Field label="Owner" help="This member becomes the accountable owner shown throughout connector governance.">
              {({ id, "aria-describedby": ariaDescribedBy }) => (
                <>
                  <Select
                    id={id}
                    aria-describedby={ariaDescribedBy}
                    value={ownerUserId}
                    onChange={(event) => setOwnerUserId(event.target.value)}
                    required
                  >
                    {users.map((user) => {
                      const identifier = userIdentifier(user);
                      const secondary = userSecondaryLabel(user);
                      return (
                        <option key={identifier} value={identifier}>
                          {userDisplayName(user)}{secondary ? ` — ${secondary}` : ""}
                        </option>
                      );
                    })}
                  </Select>
                </>
              )}
            </Field>
          ) : null}

          {action === "governance" ? (
            <div className="enterprise-admin-connector-action-fields">
              <Field label="Business purpose" help="Explain why this read-only connection is authorized.">
                {({ id, "aria-describedby": ariaDescribedBy }) => (
                  <TextInput
                    id={id}
                    aria-describedby={ariaDescribedBy}
                    value={businessPurpose}
                    onChange={(event) => setBusinessPurpose(event.target.value)}
                    placeholder="Read-only compliance evidence collection"
                  />
                )}
              </Field>
              <Field label="Risk notes" optional help="Add operating constraints or review context only when useful.">
                {({ id, "aria-describedby": ariaDescribedBy }) => (
                  <TextArea
                    id={id}
                    aria-describedby={ariaDescribedBy}
                    value={riskNotes}
                    onChange={(event) => setRiskNotes(event.target.value)}
                    placeholder="Optional governance notes"
                    rows={3}
                  />
                )}
              </Field>
            </div>
          ) : null}

          {needsReason ? (
            <Field
              label={action === "disconnect" ? "Disconnect reason" : "Reason"}
              help={action === "disconnect" ? "A reason is required and will be recorded in administrative audit." : "This context will be preserved with the administrative event."}
            >
              {({ id, "aria-describedby": ariaDescribedBy }) => (
                <TextArea
                  id={id}
                  aria-describedby={ariaDescribedBy}
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  placeholder={action === "disconnect" ? "Why this connection is being removed" : "Reason for this connector action"}
                  rows={3}
                  required={action === "disconnect"}
                  autoFocus
                />
              )}
            </Field>
          ) : null}

          {action === "disconnect" ? (
            <Alert tone="danger" title="OASYS will stop contacting the provider.">
              The provider-side authorization will not be deleted or modified. Historical evidence, governance context, and audit history remain available.
            </Alert>
          ) : null}

          {error ? (
            <Alert tone="danger" title="Action could not be completed">
              {error}
            </Alert>
          ) : null}
        </ModalSection>

        <ActionGroup className="enterprise-admin-user-modal-actions" align="end">
          <Button type="button" variant="secondary" disabled={saving} onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant={destructive ? "danger" : "primary"} disabled={submitDisabled}>
            {saving
              ? "Applying change…"
              : action === "disconnect"
                ? "Disconnect connector"
                : action === "reverify"
                  ? "Run live reverify"
                  : action === "owner"
                    ? "Assign owner"
                    : action === "governance"
                      ? "Save governance"
                      : "Confirm"}
          </Button>
        </ActionGroup>
      </form>
    </ModalShell>
  );
}
